import { createFileRoute } from "@tanstack/react-router";

import { InstagramPageContent } from "@/components/site/Instagram";
import { CtaBand, PageHero, Prose } from "@/components/site/Page";
import { getInstagramFeed } from "@/lib/instagram.functions";
import { INSTAGRAM_URL } from "@/lib/instagram";
import { seoLinks, absoluteUrl, ogImage } from "@/lib/seo";
import { safeLoader } from "@/lib/safe-loader";

const TITLE = "Instagram – Swedish International School i Estepona";
const DESCRIPTION =
  "Följ skolans vardag i Estepona och Marbella: bilder och reels från lektioner, idrott, utflykter och evenemang på svenska skolan på Costa del Sol.";

export const Route = createFileRoute("/instagram")({
  loader: () => safeLoader("sv.instagram", () => getInstagramFeed(), { posts: [], highlights: [] }),
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: absoluteUrl("/instagram") },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:locale", content: "sv_SE" },
      ...ogImage(),
    ],
    links: seoLinks("/instagram"),
  }),
  errorComponent: () => (
    <div className="mx-auto max-w-3xl px-5 py-20 text-sm text-muted-foreground">
      Flödet kunde inte laddas just nu. Försök igen om en stund.
    </div>
  ),
  notFoundComponent: () => (
    <div className="mx-auto max-w-3xl px-5 py-20 text-sm text-muted-foreground">Sidan finns inte.</div>
  ),
  component: InstagramPage,
});

function InstagramPage() {
  const { posts, highlights } = Route.useLoaderData();
  return (
    <>
      <PageHero
        eyebrow="Instagram"
        title="Skolans vardag i bilder"
        intro="På Instagram delar vi det som händer under skoldagen – lektioner, idrott, utflykter och evenemang. Här samlar vi flödet så att du kan följa oss även utan konto."
      />

      <InstagramPageContent posts={posts} highlights={highlights} locale="sv" />

      <Prose>
        <h2>Varför vi delar skolvardagen</h2>
        <p>
          Många familjer hittar till oss långt innan de flyttat till Spanien. Bilderna från
          skoldagen ger en ärlig bild av hur det ser ut hos oss: små grupper, svensk läroplan och
          en skolgård där barnen är ute stora delar av året. Vi publicerar bara sådant som faktiskt
          har hänt på skolan, och vi har alltid vårdnadshavarnas samtycke innan barn syns på bild.
        </p>
        <h2>Följ oss</h2>
        <p>
          Kontot uppdateras löpande under terminerna.{" "}
          <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer">
            Följ @swedish_international_school
          </a>{" "}
          för reels från idrotten, bilder från klassrummen och information inför terminsstart.
        </p>
      </Prose>

      <CtaBand locale="sv" />
    </>
  );
}
